// GameCenterSceneDOM.js - Complete Game Center with Template Implementation
// Integrates desktop-game-center.html + mobile-game-launcher.html templates

import BaseMenuScene from "./BaseMenuScene.js";
import { auth } from "../firebaseConfig.js";
import { createSidebarNavigation } from "../components/SharedSidebarNavigation.js";
import templateOverlayManager from "../utils/TemplateOverlayManager.js";
import { baseSceneIntegration } from "../services/BaseSceneIntegration.js";
import { addCriticalNavigationCSS } from "../utils/CriticalNavigationCSS.js";

export default class GameCenterSceneDOM extends BaseMenuScene {
    constructor() {
        super({ key: "GameCenterSceneDOM" });
        this.domElement = null;
        this.isMobile = window.innerWidth <= 768;
        this.games = [];
        this.currentFilter = 'all';
    }

    async create() {
        console.log('🎮 GameCenterSceneDOM: Creating complete game center with templates');
        
        // Load games data
        await this.loadGames();
        
        // Create the game center interface with exact template structure
        this.createGameCenterInterface();
        
        // Load EXACT same CSS as Dashboard and add Game Center content styling
        this.loadGameCenterCSS();
        
        // Initialize with enterprise services (replaces duplicate event binding)
        baseSceneIntegration.initializeTemplateScene(this, {
            overlayId: 'auth-menu-dom-overlay',
            bindEvents: true,
            enableNavigation: true
        });
        
        this.sceneReady = true;
    }

    async loadGames() {
        // Game data with current status
        this.games = [
            {
                id: 'ppt-hero',
                title: 'PPT Hero',
                description: 'Master PowerPoint shortcuts and alignment techniques',
                icon: '🎯',
                status: 'active',
                progress: 85,
                bestScore: 83500,
                sessions: 26,
                category: 'productivity',
                difficulty: 'intermediate',
                estimatedTime: '15-30 min'
            },
            {
                id: 'philosophy-flow',
                title: 'Philosophy Flow',
                description: 'AI-powered reflection and critical thinking development',
                icon: '🧠',
                status: 'active',
                progress: 45,
                bestScore: 0,
                sessions: 3,
                category: 'thinking',
                difficulty: 'advanced',
                estimatedTime: '20-45 min'
            },
            {
                id: 'slide-wars',
                title: 'Slide Wars',
                description: 'Competitive presentation building under time pressure',
                icon: '⚔️',
                status: 'coming-soon',
                progress: 0,
                bestScore: 0,
                sessions: 0,
                category: 'creativity',
                difficulty: 'expert',
                estimatedTime: '30-60 min'
            },
            {
                id: 'shortcut-mastery',
                title: 'Shortcut Mastery',
                description: 'Master keyboard shortcuts across all applications',
                icon: '⚡',
                status: 'coming-soon',
                progress: 0,
                bestScore: 0,
                sessions: 0,
                category: 'productivity',
                difficulty: 'beginner',
                estimatedTime: '10-20 min'
            },
            {
                id: 'classify-tech',
                title: 'Classify Tech',
                description: 'Technology categorization and analysis training',
                icon: '🔬',
                status: 'coming-soon',
                progress: 0,
                bestScore: 0,
                sessions: 0,
                category: 'analysis',
                difficulty: 'intermediate',
                estimatedTime: '15-25 min'
            }
        ];
    }

    createGameCenterInterface() {
        // CRITICAL: Add global critical CSS for all navigation
        addCriticalNavigationCSS();
        
        // Remove existing overlay
        const existingOverlay = document.getElementById('auth-menu-dom-overlay');
        if (existingOverlay) {
            existingOverlay.remove();
        }

        const overlay = document.createElement('div');
        overlay.id = 'auth-menu-dom-overlay';  // SAME ID as Dashboard
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            z-index: 10000;
            overflow: hidden;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            color: rgba(255, 255, 255, 0.9);
            box-sizing: border-box;
        `;

        // Load Game Center CSS (already called in create method)

        if (this.isMobile) {
            overlay.innerHTML = this.createMobileGameCenterHTML();
        } else {
            overlay.innerHTML = this.createDesktopGameCenterHTML();
        }

        document.body.appendChild(overlay);
        this.domElement = overlay;
        
        // Register with overlay manager for proper cleanup
        templateOverlayManager.registerOverlay('auth-menu-dom-overlay', 'GameCenterSceneDOM');
    }


    loadGameCenterCSS() {
        console.log('🎨 Loading Game Center CSS...');
        
        // Load EXACT same CSS as Dashboard
        if (!document.getElementById('desktop-executive-dashboard-exact-css')) {
            const link = document.createElement('link');
            link.id = 'desktop-executive-dashboard-exact-css';
            link.rel = 'stylesheet';
            link.href = '/desktop-executive-dashboard-exact.css'; // NO query parameter
            link.onload = () => console.log('✅ Desktop template CSS loaded');
            link.onerror = () => console.warn('⚠️ Desktop template CSS failed - using critical CSS fallback');
            document.head.appendChild(link);
        }
        
        console.log('🎨 All Game Center CSS loaded');
    }

    // REMOVED: 37 lines of duplicate navigation logic
    // Now handled by baseSceneIntegration.initializeTemplateScene()
    // Navigation actions are centralized in NavigationActionHandler service

    createDesktopGameCenterHTML() {
        const activeGames = this.games.filter(g => g.status === 'active').length;
        
        return `
            <!-- Animated Background (SAME as Dashboard) -->
            <div class="background-effects">
                <div class="bg-grid"></div>
                <div class="bg-glow bg-glow-1"></div>
                <div class="bg-glow bg-glow-2"></div>
            </div>
            
            <!-- Layout Container (SAME as Dashboard) -->
            <div class="app-container">
                <!-- Sidebar Navigation (EXACT Dashboard structure) -->
                <aside class="sidebar">
                    <div class="sidebar-header">
                        <div class="logo">
                            <div class="logo-icon">D</div>
                            <div class="logo-text">
                                <div>DeckOps.ai</div>
                                <div class="logo-subtitle">Executive Platform</div>
                            </div>
                        </div>
                    </div>
                    
                    <nav class="nav-menu">
                        <div class="nav-section">
                            <div class="nav-section-title">Main</div>
                            <a href="#" class="nav-item" data-action="dashboard">
                                <span class="nav-icon">🏠</span>
                                <span class="nav-label">Dashboard</span>
                            </a>
                            <a href="#" class="nav-item" data-action="analytics">
                                <span class="nav-icon">📊</span>
                                <span class="nav-label">Analytics</span>
                            </a>
                            <a href="#" class="nav-item" data-action="ppt-hero-analytics">
                                <span class="nav-icon">📈</span>
                                <span class="nav-label">PPT Hero Analytics</span>
                                <span class="nav-badge">Detailed</span>
                            </a>
                            <a href="#" class="nav-item" data-action="hall-of-fame">
                                <span class="nav-icon">🏆</span>
                                <span class="nav-label">Hall of Fame</span>
                                <span class="nav-badge">New</span>
                            </a>
                        </div>
                    
                        <div class="nav-section">
                            <div class="nav-section-title">Training Modules</div>
                            <a href="#" class="nav-item active" data-action="game-center">
                                <span class="nav-icon">🎮</span>
                                <span class="nav-label">Game Center</span>
                            </a>
                            <a href="#" class="nav-item" data-game="ppt-hero">
                                <span class="nav-icon">📈</span>
                                <span class="nav-label">PPT Hero</span>
                            </a>
                            <a href="#" class="nav-item" data-game="philosophy-flow">
                                <span class="nav-icon">🎨</span>
                                <span class="nav-label">Philosophy Flow</span>
                            </a>
                            <a href="#" class="nav-item" data-game="slide-wars">
                                <span class="nav-icon">⚔️</span>
                                <span class="nav-label">Slide Wars</span>
                            </a>
                            <a href="#" class="nav-item" data-game="classify-tech">
                                <span class="nav-icon">🏛️</span>
                                <span class="nav-label">Classify Tech</span>
                            </a>
                        </div>
                        
                        <div class="nav-section">
                            <div class="nav-section-title">Settings</div>
                            <a href="#" class="nav-item" data-action="profile">
                                <span class="nav-icon">⚙️</span>
                                <span class="nav-label">Preferences</span>
                            </a>
                            <a href="#" class="nav-item" data-action="help">
                                <span class="nav-icon">❓</span>
                                <span class="nav-label">Help & Support</span>
                            </a>
                        </div>
                    </nav>
                    
                    <div class="sidebar-footer">
                        <div class="user-profile">
                            <div class="user-avatar">SH</div>
                            <div class="user-info">
                                <div class="user-name">Shane Hercules</div>
                                <div class="user-role">Executive</div>
                            </div>
                            <span style="color: var(--text-muted);">›</span>
                        </div>
                    </div>
                </aside>
                
                <!-- Main Content -->
                <main class="main-content">
                    <!-- Header with breadcrumb -->
                    <header class="header">
                        <div class="header-left">
                            <h1 class="page-title">Game Center</h1>
                            <div class="breadcrumb">
                                <span>Home</span>
                                <span class="breadcrumb-separator">/</span>
                                <span>Game Center</span>
                            </div>
                        </div>
                        <div class="header-right">
                            <div class="header-stats">
                                <div class="stat-item">
                                    <span class="stat-value">${activeGames}</span>
                                    <span class="stat-label">Active Games</span>
                                </div>
                                <div class="stat-item">
                                    <span class="stat-value">${this.getTotalSessions()}</span>
                                    <span class="stat-label">Total Sessions</span>
                                </div>
                            </div>
                        </div>
                    </header>
                    
                    <!-- Filter Tabs -->
                    <div class="filter-tabs">
                        <button class="filter-tab active" data-filter="all">All Games</button>
                        <button class="filter-tab" data-filter="active">Active</button>
                        <button class="filter-tab" data-filter="productivity">Productivity</button>
                        <button class="filter-tab" data-filter="thinking">Thinking</button>
                        <button class="filter-tab" data-filter="creativity">Creativity</button>
                    </div>
                    
                    <!-- Games Grid -->
                    <div class="games-grid">
                        ${this.createGamesGrid()}
                    </div>
                </main>
            </div>
        `;
    }

    createMobileGameCenterHTML() {
        return `
            ${createTemplateHeader('Game Center', ['Home', 'Dashboard', 'Games'])}
            
            <!-- Mobile Game Center Template Structure -->
            <header class="header">
                <div class="header-left">
                    <button class="menu-button" data-action="menu">
                        <span>☰</span>
                    </button>
                    <h1 class="header-title">Game Center</h1>
                </div>
                <div class="header-right">
                    <div class="points-badge">
                        <span class="points-icon">💎</span>
                        <span class="points-value">1,247</span>
                    </div>
                </div>
            </header>
            
            <div class="content">
                <!-- Quick Stats -->
                <div class="quick-stats">
                    <div class="stat-card">
                        <div class="stat-icon">🎯</div>
                        <div class="stat-content">
                            <div class="stat-value">${this.getTotalSessions()}</div>
                            <div class="stat-label">Sessions</div>
                        </div>
                    </div>
                    <div class="stat-card">
                        <div class="stat-icon">📈</div>
                        <div class="stat-content">
                            <div class="stat-value">${this.getAverageProgress()}%</div>
                            <div class="stat-label">Progress</div>
                        </div>
                    </div>
                    <div class="stat-card">
                        <div class="stat-icon">🏆</div>
                        <div class="stat-content">
                            <div class="stat-value">#1</div>
                            <div class="stat-label">Rank</div>
                        </div>
                    </div>
                </div>
                
                <!-- Games List -->
                <div class="games-list">
                    ${this.createMobileGamesList()}
                </div>
            </div>
        `;
    }

    createGamesGrid() {
        return this.getFilteredGames().map(game => `
            <div class="game-card ${game.status}" data-game="${game.id}">
                <div class="game-header">
                    <div class="game-icon">${game.icon}</div>
                    <div class="game-status-badge ${game.status}">${this.formatStatus(game.status)}</div>
                </div>
                
                <div class="game-content">
                    <h3 class="game-title">${game.title}</h3>
                    <p class="game-description">${game.description}</p>
                    
                    <div class="game-stats">
                        <div class="stat-row">
                            <span class="stat-label">Progress</span>
                            <div class="progress-bar">
                                <div class="progress-fill" style="width: ${game.progress}%"></div>
                            </div>
                            <span class="stat-value">${game.progress}%</span>
                        </div>
                        
                        ${game.status === 'active' ? `
                            <div class="stat-row">
                                <span class="stat-label">Best Score</span>
                                <span class="stat-value">${game.bestScore.toLocaleString()}</span>
                            </div>
                            <div class="stat-row">
                                <span class="stat-label">Sessions</span>
                                <span class="stat-value">${game.sessions}</span>
                            </div>
                        ` : `
                            <div class="stat-row">
                                <span class="stat-label">Difficulty</span>
                                <span class="stat-value difficulty-${game.difficulty}">${game.difficulty}</span>
                            </div>
                            <div class="stat-row">
                                <span class="stat-label">Est. Time</span>
                                <span class="stat-value">${game.estimatedTime}</span>
                            </div>
                        `}
                    </div>
                </div>
                
                <div class="game-actions">
                    ${game.status === 'active' ? `
                        <button class="btn-secondary" data-action="view-stats" data-game="${game.id}">View Stats</button>
                        <button class="btn-primary" data-action="play-game" data-game="${game.id}">Continue</button>
                    ` : `
                        <button class="btn-disabled" disabled>Coming Soon</button>
                    `}
                </div>
            </div>
        `).join('');
    }

    createMobileGamesList() {
        return this.games.map(game => `
            <div class="game-item ${game.status}" data-game="${game.id}">
                <div class="game-icon-mobile">${game.icon}</div>
                <div class="game-info">
                    <div class="game-header-mobile">
                        <h3 class="game-title-mobile">${game.title}</h3>
                        <span class="game-status-mobile ${game.status}">${this.formatStatus(game.status)}</span>
                    </div>
                    <p class="game-description-mobile">${game.description}</p>
                    
                    ${game.status === 'active' ? `
                        <div class="game-progress">
                            <div class="progress-info">
                                <span class="progress-label">Progress: ${game.progress}%</span>
                                <span class="progress-sessions">${game.sessions} sessions</span>
                            </div>
                            <div class="progress-bar-mobile">
                                <div class="progress-fill-mobile" style="width: ${game.progress}%"></div>
                            </div>
                        </div>
                    ` : `
                        <div class="game-meta">
                            <span class="game-difficulty ${game.difficulty}">${game.difficulty}</span>
                            <span class="game-time">${game.estimatedTime}</span>
                        </div>
                    `}
                </div>
                
                <div class="game-action-mobile">
                    ${game.status === 'active' ? `
                        <button class="play-btn" data-action="play-game" data-game="${game.id}">
                            <span>▶️</span>
                        </button>
                    ` : `
                        <div class="coming-soon-badge">Soon</div>
                    `}
                </div>
            </div>
        `).join('');
    }

    getFilteredGames() {
        switch (this.currentFilter) {
            case 'active':
                return this.games.filter(g => g.status === 'active');
            case 'productivity':
            case 'thinking':
            case 'creativity':
            case 'analysis':
                return this.games.filter(g => g.category === this.currentFilter);
            default:
                return this.games;
        }
    }

    getTotalSessions() {
        return this.games.reduce((total, game) => total + game.sessions, 0);
    }

    getAverageProgress() {
        const activeGames = this.games.filter(g => g.status === 'active');
        if (activeGames.length === 0) return 0;
        return Math.round(activeGames.reduce((total, game) => total + game.progress, 0) / activeGames.length);
    }

    formatStatus(status) {
        switch (status) {
            case 'active': return 'Active';
            case 'coming-soon': return 'Coming Soon';
            case 'beta': return 'Beta';
            default: return status;
        }
    }

    setupEventListeners() {
        // NOTE: Navigation events are handled by bindExactDashboardEvents() - no duplicate binding here

        // Filter actions
        this.domElement.querySelectorAll('[data-filter]').forEach(filter => {
            filter.addEventListener('click', (e) => {
                e.preventDefault();
                this.handleFilter(filter.dataset.filter);
            });
        });

        // Game card clicks
        this.domElement.querySelectorAll('.game-card, .game-item').forEach(card => {
            card.addEventListener('click', (e) => {
                if (!e.target.closest('button')) {
                    this.handleGameSelect(card.dataset.game);
                }
            });
        });
    }

    // Removed conflicting handleAction method - using handleTemplateAction instead

    handleFilter(filter) {
        this.currentFilter = filter;
        
        // Update filter appearance
        this.domElement.querySelectorAll('[data-filter]').forEach(filterEl => {
            filterEl.classList.toggle('active', filterEl.dataset.filter === filter);
        });
        
        // Refresh games list
        const container = this.domElement.querySelector('.games-grid, .games-list');
        if (container) {
            container.innerHTML = this.isMobile ? this.createMobileGamesList() : this.createGamesGrid();
        }
        
        // Re-bind event listeners
        this.setupEventListeners();
    }

    handleGameSelect(gameId) {
        const game = this.games.find(g => g.id === gameId);
        if (game && game.status === 'active') {
            this.playGame(gameId);
        }
    }

    playGame(gameId) {
        console.log('🎮 Starting game:', gameId);
        
        switch (gameId) {
            case 'ppt-hero':
                this.startScene('PPTHeroGenericScene');
                break;
            case 'philosophy-flow':
                this.startScene('PhilosophyFlowScene');
                break;
            case 'slide-wars':
                this.showNotification('Slide Wars coming soon!', 'info');
                break;
            case 'shortcut-mastery':
                this.showNotification('Shortcut Mastery coming soon!', 'info');
                break;
            case 'classify-tech':
                this.startScene('ClassifyScene');
                break;
            default:
                this.showNotification('Game not available', 'error');
        }
    }

    viewGameStats(gameId) {
        console.log('📊 Viewing stats for game:', gameId);
        // Could open analytics or specific game stats
        this.openAnalytics();
    }

    openMobileMenu() {
        // Open mobile navigation menu
        console.log('📱 Opening mobile menu');
    }

    async openAnalytics() {
        const { populateAnalyticsOverlay } = await import("../ui/analyticsUI.js");
        await populateAnalyticsOverlay(auth.currentUser.uid, "PPTHero");
        window.UIController.showOverlay('analytics-overlay');
    }

    async openHallOfFame() {
        console.log('🏆 Opening new desktop Hall of Fame template...');
        this.startScene('HallOfFameDesktopTemplate');
    }

    async handleLogout() {
        try {
            await auth.signOut();
            this.startScene('GuestMenuSceneDOM');
        } catch (error) {
            console.error('Logout error:', error);
            this.showNotification('Error logging out. Please try again.', 'error');
        }
    }

    showNotification(message, type = 'info') {
        console.log(`🎮 Notification (${type}):`, message);
        if (window.showGeneralMessage) {
            window.showGeneralMessage(message, 3000, type === 'error');
        }
    }

    shutdown() {
        console.log('🎮 GameCenterSceneDOM: Shutting down and cleaning up overlays...');
        
        if (this.domElement) {
            this.domElement.remove();
            this.domElement = null;
        }
        
        // Clean up game center scene overlay
        const gameCenterOverlay = document.getElementById('game-center-scene-overlay');
        if (gameCenterOverlay) {
            gameCenterOverlay.remove();
            console.log('🎮 GameCenterSceneDOM: Overlay removed');
        }
        
        super.shutdown();
    }
}
